import { z } from "zod";

// ---------------------------------------------------------------------------
// قاعده‌های زاد برای مقدار رشته‌ای فیلدهای فرم (ارقام فارسی/عربی/لاتین هر سه پذیرفته).
// فقط اعتبارسنجی می‌کنند و مقدار را تغییر نمی‌دهند؛ تبدیل به عدد/payload هنگام ارسال با parseIntText.
// پیام‌ها فارسی و با ارقام فارسی‌اند.
// ---------------------------------------------------------------------------

const faDigits = "۰۱۲۳۴۵۶۷۸۹";
const arDigits = "٠١٢٣٤٥٦٧٨٩";
const faPlain = new Intl.NumberFormat("fa-IR", { useGrouping: false });
const faGrouped = new Intl.NumberFormat("fa-IR");

export const toEnDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (d) => String(faDigits.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(arDigits.indexOf(d)));

/**
 * «1000000» یا «۱۰۰۰۰۰۰» → «۱٬۰۰۰٬۰۰۰»؛ نویسه‌ی غیررقمی دور ریخته می‌شود (رشته‌ای، پس عدد بزرگ هم
 * گرد نمی‌شود). مگر علامت اعشار یا منفی («1.5»، «۱/۵»، «-5»): حذف بی‌صدایشان «1.5 میلیون» را «۱۵
 * میلیون» می‌کرد، پس متن همان‌طور که تایپ شده می‌ماند و intText/moneyText خطای «باید عدد باشد» می‌دهد.
 */
export const groupDigits = (value: string) =>
  /[.\-−–٫/]/.test(value)
    ? value
    : toEnDigits(value)
        .replace(/\D/g, "")
        .replace(/\B(?=(\d{3})+(?!\d))/g, "٬")
        .replace(/\d/g, (d) => faDigits.charAt(Number(d)));

/** عدد صحیح از متن با ارقام فارسی و جداکننده هزارگان؛ متن نامعتبر یا خالی = null */
export const parseIntText = (value: string): number | null => {
  const clean = toEnDigits(value).replace(/[,٬،\s]/g, "");
  if (!/^-?\d+$/.test(clean)) return null;
  const number = Number(clean);
  return Number.isSafeInteger(number) ? number : null;
};

export type TextRule = {
  /** کمینه حرف (پیش‌فرض ۲) */
  min?: number;
  max?: number;
};

/** متن الزامی */
export const requiredText = (label: string, rule: TextRule = {}) => {
  const min = rule.min ?? 2;
  let schema = z
    .string()
    .trim()
    .min(
      min,
      min <= 1 ? `${label} را وارد کنید` : `${label} باید دست‌کم ${faPlain.format(min)} حرف باشد`,
    );
  if (rule.max !== undefined) {
    schema = schema.max(rule.max, `${label} حداکثر ${faPlain.format(rule.max)} حرف است`);
  }
  return schema;
};

/** متن اختیاری با سقف طول */
export const optionalText = (label: string, max = 2000) =>
  z.string().max(max, `${label} حداکثر ${faPlain.format(max)} حرف است`);

/** انتخاب الزامی از فهرست (گزینه خالی = انتخاب‌نشده) */
export const requiredChoice = (label: string) => z.string().min(1, `${label} را انتخاب کنید`);

export type IntRule = {
  min?: number;
  max?: number;
  /** خالی مجاز است */
  optional?: boolean;
  /** نمایش با جداکننده هزارگان در پیام بازه (برای مبلغ و کیلومتر) */
  grouped?: boolean;
};

/** عدد صحیح در بازه؛ ارقام فارسی و جداکننده پذیرفته */
export const intText = (label: string, rule: IntRule = {}) =>
  z.string().superRefine((value, context) => {
    if (value.trim() === "") {
      if (!rule.optional) context.addIssue({ code: "custom", message: `${label} را وارد کنید` });
      return;
    }
    const number = parseIntText(value);
    if (number === null) {
      context.addIssue({ code: "custom", message: `${label} باید عدد باشد` });
      return;
    }
    const format = rule.grouped ? faGrouped : faPlain;
    if (rule.min !== undefined && rule.max !== undefined) {
      if (number < rule.min || number > rule.max) {
        context.addIssue({
          code: "custom",
          message: `${label} باید بین ${format.format(rule.min)} تا ${format.format(rule.max)} باشد`,
        });
      }
      return;
    }
    if (rule.min !== undefined && number < rule.min) {
      context.addIssue({
        code: "custom",
        message: `${label} باید دست‌کم ${format.format(rule.min)} باشد`,
      });
    }
    if (rule.max !== undefined && number > rule.max) {
      context.addIssue({
        code: "custom",
        message: `${label} حداکثر ${format.format(rule.max)} است`,
      });
    }
  });

/** مبلغ به تومان؛ پیش‌فرض بزرگ‌تر از صفر */
export const moneyText = (label: string, rule: IntRule = {}) =>
  intText(label, { min: 1, ...rule, grouped: true });

export type YearRule = { min: number; max: number; optional?: boolean };

/**
 * سال مدل میلادی. اگر کاربر سال شمسی (۱۳۰۰ تا ۱۵۰۰) بنویسد پیام راهنما می‌گیرد، نه فقط «خارج از بازه».
 * بازه از @car/api/model-year می‌آید تا با سرور یکی باشد.
 */
export const modelYearText = (label: string, rule: YearRule) =>
  z.string().superRefine((value, context) => {
    if (value.trim() === "") {
      if (!rule.optional) context.addIssue({ code: "custom", message: `${label} را وارد کنید` });
      return;
    }
    const year = parseIntText(value);
    if (year === null) {
      context.addIssue({ code: "custom", message: `${label} باید عدد باشد` });
      return;
    }
    if (year >= 1300 && year <= 1500) {
      context.addIssue({
        code: "custom",
        message: `${label} را میلادی وارد کنید (مثلاً ${faPlain.format(year + 621)})`,
      });
      return;
    }
    if (year < rule.min || year > rule.max) {
      context.addIssue({
        code: "custom",
        message: `${label} باید بین ${faPlain.format(rule.min)} تا ${faPlain.format(rule.max)} باشد`,
      });
    }
  });

export type OptionalRule = { optional?: boolean };

const withOptional = (
  rule: OptionalRule,
  label: string,
  message: string,
  valid: (digits: string) => boolean,
) =>
  z.string().superRefine((value, context) => {
    const clean = toEnDigits(value.trim());
    if (clean === "") {
      if (!rule.optional) context.addIssue({ code: "custom", message: `${label} را وارد کنید` });
      return;
    }
    if (!valid(clean)) context.addIssue({ code: "custom", message });
  });

/**
 * شماره موبایل به شکل ذخیره‌شده 09xxxxxxxxx: ارقام لاتین، بی‌فاصله و خط تیره، ‎+98 / 0098 / 98 / 9xx با ۰.
 * هم‌سان با normalizePhone سرور در @car/auth/roles (apps/server/test/phone.test.ts هر دو را مقایسه می‌کند).
 */
export const normalizeMobile = (value: string) => {
  const digits = toEnDigits(value).replace(/\D/g, "");
  if (digits.startsWith("0098")) return `0${digits.slice(4)}`;
  if (digits.startsWith("98") && digits.length === 12) return `0${digits.slice(2)}`;
  if (digits.startsWith("9") && digits.length === 10) return `0${digits}`;
  return digits;
};

/** موبایل ایران؛ فاصله، خط تیره و پیشوند کشور پذیرفته و روی مقدار یکسان‌شده سنجیده می‌شود */
export const mobileText = (rule: OptionalRule = {}) =>
  withOptional(rule, "شماره موبایل", "شماره موبایل معتبر نیست؛ مثلاً ۰۹۱۲۱۲۳۴۵۶۷", (text) =>
    /^09\d{9}$/.test(normalizeMobile(text)),
  );

/** هر شماره تماس (ثابت یا همراه) ۵ تا ۲۰ رقم؛ هم‌سان با سرور CRM که تلفن ثابت را هم می‌پذیرد */
export const phoneText = (rule: OptionalRule = {}) =>
  withOptional(rule, "شماره تماس", "شماره تماس باید ۵ تا ۲۰ رقم باشد", (text) =>
    /^\+?\d{5,20}$/.test(text.replace(/[\s()-]/g, "")),
  );

/** شماره تماس سرنخ: موبایل یا تلفن ثابت با پیش‌شماره، ۱۱ رقم؛ هم‌سان با isLeadPhone سرور در @car/api/crm-config */
export const leadPhoneText = (rule: OptionalRule = {}) =>
  withOptional(
    rule,
    "شماره تماس",
    "شماره تماس ۱۱ رقم است؛ موبایل مثل ۰۹۱۲۱۲۳۴۵۶۷ یا ثابت با پیش‌شماره مثل ۰۷۶۴۴۴۲۱۲۳۴",
    (text) => /^0\d{10}$/.test(normalizeMobile(text)),
  );

/**
 * کد ملی ۱۰ رقمی با رقم کنترل (رقم آخر)؛ همه‌رقم‌یکسان مثل ۰۰۰۰۰۰۰۰۰۰ پذیرفته نیست.
 * هم‌سان با isNationalId سرور در @car/api/ownership-config (apps/server/test/national-id.test.ts هر دو را مقایسه می‌کند).
 */
const nationalIdChecks = (digits: string) => {
  if (!/^\d{10}$/.test(digits) || /^(\d)\1{9}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += Number(digits[i]) * (10 - i);
  const r = sum % 11;
  return Number(digits[9]) === (r < 2 ? r : 11 - r);
};

export const nationalIdText = (rule: OptionalRule = {}) =>
  withOptional(rule, "کد ملی", "کد ملی معتبر نیست؛ ده رقم را دوباره بررسی کنید", nationalIdChecks);

export const emailText = (rule: OptionalRule = {}) =>
  withOptional(rule, "ایمیل", "نشانی ایمیل معتبر نیست", (text) => /^\S+@\S+\.\S+$/.test(text));
